import { useState, useEffect, useCallback } from "react";
import { campaignAPI } from "../services/api";

export const useCampaigns = (status = null, limit = 20, offset = 0) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await campaignAPI.getCampaigns(status, limit, offset);
      setCampaigns(response.data.campaigns || []);
      setTotalCount(response.data.total_count || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  }, [status, limit, offset]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const createCampaign = async (campaignData) => {
    try {
      const response = await campaignAPI.createCampaign(campaignData);
      await fetchCampaigns(); // Refresh campaigns
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Failed to create campaign",
      };
    }
  };

  const updateCampaign = async (campaignId, campaignData) => {
    try {
      const response = await campaignAPI.updateCampaign(
        campaignId,
        campaignData,
      );
      await fetchCampaigns(); // Refresh campaigns
      return { success: true, data: response.data };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Failed to update campaign",
      };
    }
  };

  const deleteCampaign = async (campaignId) => {
    try {
      await campaignAPI.deleteCampaign(campaignId);
      await fetchCampaigns(); // Refresh campaigns
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Failed to delete campaign",
      };
    }
  };

  return {
    campaigns,
    loading,
    error,
    totalCount,
    refetch: fetchCampaigns,
    createCampaign,
    updateCampaign,
    deleteCampaign,
  };
};

export const useCampaign = (campaignId) => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId) return;

    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await campaignAPI.getCampaign(campaignId);
        setCampaign(response.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch campaign");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [campaignId]);

  return { campaign, loading, error };
};
